import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Check, Menu, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import GenieMark from "@/components/Common/GenieMark";
import { PageWrapper } from "@/components/PageWrapper";
import HelpChatPanel from "@/components/SupportTicket/HelpChatPanel";
import SupportTicketModal from "@/components/SupportTicket/SupportTicketModal";
import DeleteChatModal from "./DeleteChatModal";
import {
  fetchChatConversations,
  fetchChatConversation,
  removeChatConversation,
} from "@/actions/chatHistory";
import {
  conversationStarted,
  selectConversation,
  selectNewConversation,
} from "@/reducers/chatHistory";
import {
  filterConversations,
  groupConversationsByDate,
} from "@/helpers/chatHistory";
import { renameChatConversation } from "@/services/copilotService";
import { pageURLs } from "@/constants";
import "./ChatPage.scss";

const DRAFT_CONVERSATION_ID = "draft";

// Full-page chat experience (PostHog-AI-assistant-style): a left history
// sub-panel (searchable list + "New chat") and a main panel that reuses
// HelpChatPanel for both the empty-state prompt and the active message
// thread/composer - see HelpChatPanel.js's `layout="page"` branch.
//
// On mobile the history panel becomes a slide-in drawer (opened via the
// hamburger button in the page's own header) rather than a squeezed-down
// two-pane layout - this page is still the full-page experience on mobile
// too, per the "left menu on the left and full page" requirement; only the
// entry point into this page (see MobileBottomNav wiring in
// HeaderSidebarAgent.js) differs from desktop.
const ChatPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { conversationId: routeConversationId } = useParams();

  const {
    conversations,
    conversationsLoading,
    conversationsError,
    activeConversationId,
    activeMessages,
    activeMessagesLoading,
    activeMessagesError,
  } = useSelector((state) => state.chatHistory);

  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftConversation, setDraftConversation] = useState(null);
  const [titleOverrides, setTitleOverrides] = useState({});
  const [renamingId, setRenamingId] = useState(null);
  const [renameDraft, setRenameDraft] = useState("");
  // Forces HelpChatPanel to remount only on an explicit "New chat"/history
  // selection - NOT when a brand-new conversation's id comes back from the
  // server mid-session (conversationStarted below), which must leave the
  // panel (and the reply it just streamed) alone. Deliberately not derived
  // from activeConversationId, since that value changes in both cases.
  const [panelInstance, setPanelInstance] = useState(0);
  // Conversation ids this page started itself, so the route effect above can
  // tell "the server just named the conversation I am already streaming"
  // apart from "the user picked a different conversation". Only the second
  // may remount the panel.
  const selfStartedRef = useRef(new Set());
  // Conversation ids this page has already tried to hydrate on a fresh
  // mount. Membership means "attempted", not "succeeded", which is the point:
  // a failed attempt must not be retried on every render.
  const hydrationTriedRef = useRef(new Set());
  const [ticketModal, setTicketModal] = useState({
    show: false,
    transcript: "",
  });

  useEffect(() => {
    dispatch(fetchChatConversations());
  }, [dispatch]);

  // The reducer now keeps the last-good list on a failed refetch rather
  // than wiping it to `[]` (a bug caught in review), which means the
  // failure itself is otherwise invisible - the sidebar just looks
  // unchanged. Surface it so a rename/new-chat that couldn't refresh the
  // list isn't silently indistinguishable from one that succeeded.
  useEffect(() => {
    if (conversationsError) {
      toast.error("Could not refresh chat history. Showing the last loaded list.");
    }
  }, [conversationsError]);

  // .chat-page's height was a hardcoded calc(100vh - 72px), which assumed the
  // page starts 72px down. It actually starts wherever the app header and page
  // chrome leave it (120px at the time of writing), so the pane overran the
  // viewport and the whole document scrolled - the bottom edge looked clipped.
  // Measure the real offset instead and let CSS subtract it, so this stays
  // correct if the header height ever changes or a tenant adds a banner.
  const pageRef = useRef(null);
  useEffect(() => {
    const el = pageRef.current;
    if (!el) return undefined;
    // Set the var on .chat-page-wrapper (PageWrapper's outer element) so it
    // inherits down to .chat-page - both are sized off the same offset, and
    // both were previously hardcoded to the same wrong 72px.
    const target = el.closest(".chat-page-wrapper") || el;
    const sync = () => {
      const top = target.getBoundingClientRect().top + window.scrollY;
      target.style.setProperty(
        "--chat-page-offset",
        `${Math.max(0, Math.round(top))}px`,
      );
    };
    sync();
    window.addEventListener("resize", sync);
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    if (ro && el.parentElement) ro.observe(el.parentElement);
    return () => {
      window.removeEventListener("resize", sync);
      if (ro) ro.disconnect();
    };
  }, []);

  // Keyboard avoidance. dvh follows the browser chrome collapsing but not the
  // software keyboard: on iOS the layout viewport does not resize when the
  // keyboard opens, so a pane sized to the full height keeps its composer
  // underneath it. visualViewport does report it, so measure the occluded
  // strip and let the height calc subtract it.
  useEffect(() => {
    const vv = window.visualViewport;
    const el = pageRef.current;
    if (!vv || !el) return undefined;
    const target = el.closest(".chat-page-wrapper") || el;
    const sync = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      target.style.setProperty("--chat-kb-inset", `${Math.round(inset)}px`);
    };
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
      target.style.removeProperty("--chat-kb-inset");
    };
  }, []);

  useEffect(() => {
    if (!routeConversationId) {
      // handleConversationStarted dispatches the new id and replace()s the
      // URL together, and the two do not always land in the same render -
      // the same race the selfStartedRef branch below exists for. Here it
      // shows up as this effect observing the still-bare "/chat" route
      // against the brand-new activeConversationId for one render, reading
      // it as "user navigated back to bare /chat while a conversation was
      // active" and wiping the draft/remounting the panel that is still
      // streaming that conversation's first reply - the same vanishing
      // mid-sentence bug, just from the other side of the id transition.
      if (
        activeConversationId &&
        selfStartedRef.current.has(activeConversationId)
      ) {
        selfStartedRef.current.delete(activeConversationId);
        return;
      }
      // Navigating to bare /chat (e.g. via a nav link) while a conversation
      // is still active left the panel showing that conversation with the
      // URL and sidebar out of sync - nothing previously reconciled state
      // back to "nothing selected" for this direction (a bug caught in
      // review).
      if (activeConversationId) {
        dispatch(selectNewConversation());
        setDraftConversation(null);
        setPanelInstance((n) => n + 1);
      }
      return;
    }
    if (routeConversationId === activeConversationId) {
      // ...unless this is a fresh mount of the page with nothing loaded.
      // Returning from a draft is exactly that: the panel unmounted, its
      // in-memory thread went with it, and the store still names this
      // conversation active, so every branch below reads as "already here"
      // and nothing ever fetches. The URL said one thing and the screen
      // showed the empty state. `activeMessages === null` is the reducer's
      // own "never loaded / failed" value, distinct from [] for a
      // conversation genuinely without messages.
      // A conversation this page started is the panel's own live thread, in
      // memory and mid-reply. Never fetch over it: selfStartedRef is a fresh
      // Set on every mount, so after navigating away and back it is empty
      // and the hydration below is reached, which is exactly the difference
      // between the two cases.
      if (!selfStartedRef.current.has(routeConversationId)) {
        if (
          !activeMessages &&
          !activeMessagesLoading &&
          !hydrationTriedRef.current.has(routeConversationId)
        ) {
          // Once per id, never in a loop. A conversation whose first turn
          // has not been persisted yet 404s here, the reducer leaves
          // activeMessages null to mark the failure, and without this guard
          // that same null drives the same fetch on the next render,
          // forever (caught on the happy-path walk as a wall of 404s that
          // drowned the send).
          hydrationTriedRef.current.add(routeConversationId);
          dispatch(fetchChatConversation(routeConversationId));
          return;
        }
      }
      // The route and the store have settled on the same id - whether or
      // not this render is the one where they caught up with each other.
      // Either way, any self-started exemption for this id has done its
      // job: leaving it in the set would let a later, unrelated navigation
      // to bare /chat (or away and back) skip the reset/refetch it needs,
      // exactly the stale-state bug this exemption exists to avoid causing
      // (found in review).
      selfStartedRef.current.delete(routeConversationId);
      return;
    }
    // A conversation the panel itself just started is NOT a navigation to a
    // different conversation, even though the route changed. handleConversationStarted
    // dispatches the id and replaces the URL together, and the two do not
    // always land in the same render, so this effect could observe the new
    // route against the old active id, treat it as a switch, and bump
    // panelInstance. That remounts HelpChatPanel, whose unmount cleanup
    // aborts the in-flight fetch, and the agent watches their reply vanish
    // mid-sentence. Reconcile the state, never the mount - and consume the
    // exemption immediately (found in review), or navigating away and back
    // to this same conversation later would hit this branch again and skip
    // the real fetch/remount it needs then.
    if (selfStartedRef.current.has(routeConversationId)) {
      selfStartedRef.current.delete(routeConversationId);
      dispatch(selectConversation(routeConversationId));
      return;
    }
    dispatch(selectConversation(routeConversationId));
    dispatch(fetchChatConversation(routeConversationId));
    setDraftConversation(null);
    setPanelInstance((n) => n + 1);
  }, [
    routeConversationId,
    activeConversationId,
    activeMessages,
    activeMessagesLoading,
    dispatch,
  ]);

  const listedConversations = useMemo(() => {
    const withOverrides = conversations.map((c) =>
      titleOverrides[c.conversationId]
        ? { ...c, title: titleOverrides[c.conversationId] }
        : c,
    );
    return draftConversation
      ? [draftConversation, ...withOverrides]
      : withOverrides;
  }, [conversations, draftConversation, titleOverrides]);

  const filteredConversations = useMemo(
    () => filterConversations(listedConversations, search),
    [listedConversations, search],
  );

  // Grouped by recency so a run of identically-titled chats is still
  // distinguishable. Buckets with nothing in them are dropped by the helper.
  const conversationGroups = useMemo(
    () => groupConversationsByDate(filteredConversations),
    [filteredConversations],
  );

  const handleNewChat = useCallback(() => {
    dispatch(selectNewConversation());
    setDraftConversation(null);
    setPanelInstance((n) => n + 1);
    setDrawerOpen(false);
    if (routeConversationId) navigate(pageURLs.agentChat);
  }, [dispatch, navigate, routeConversationId]);

  const handleConversationDraft = useCallback((title) => {
    setDraftConversation({
      conversationId: DRAFT_CONVERSATION_ID,
      title,
      updatedAt: null,
    });
  }, []);

  // The first turn of a brand-new conversation failed before the server
  // ever handed back a real id, so the optimistic draft row above is now
  // orphaned (a bug caught in review) - clear it so the sidebar goes back
  // to reflecting only conversations that actually exist. HelpChatPanel
  // itself stays mounted (the failed message and its retry affordance are
  // still visible), only the sidebar's placeholder row is removed.
  const handleConversationDraftFailed = useCallback(() => {
    setDraftConversation(null);
  }, []);

  // Guards a single rename session against being committed twice - clicking
  // the Save button blurs the input (firing the new onBlur handler) *before*
  // the button's own onClick runs, so without this both would call
  // commitRename for the same session and double-fire the rename API call.
  const renameCommittedRef = useRef(false);

  const beginRename = useCallback((conversation) => {
    renameCommittedRef.current = false;
    setRenamingId(conversation.conversationId);
    setRenameDraft(conversation.title || "");
  }, []);

  const clearTitleOverride = useCallback((conversationId) => {
    setTitleOverrides((map) => {
      if (!(conversationId in map)) return map;
      const next = { ...map };
      delete next[conversationId];
      return next;
    });
  }, []);

  const commitRename = useCallback(
    async (conversation) => {
      if (renameCommittedRef.current) return;
      renameCommittedRef.current = true;
      const title = renameDraft.trim();
      setRenamingId(null);
      if (!title || title === conversation.title) return;
      setTitleOverrides((map) => ({
        ...map,
        [conversation.conversationId]: title,
      }));
      try {
        await renameChatConversation(conversation.conversationId, title);
      } catch (err) {
        // Deleting the override (rather than setting it back to the
        // pre-rename title) matters when the PATCH actually succeeded
        // server-side but the client only lost the response (timeout/502):
        // setting it to the stale title would permanently mask the server's
        // real, already-renamed title (a bug caught in review). Deleting it
        // instead falls back to whatever `conversations` says, which a
        // subsequent list refetch can still correct either way.
        clearTitleOverride(conversation.conversationId);
        toast.error(
          err?.response?.data?.message || "Could not rename this chat. Try again.",
        );
        return;
      }
      // The override masks conversations[].title until this refetch
      // resolves. It used to never get removed after that, permanently
      // hiding the server's own title (including any further server-side
      // change) behind this optimistic value forever (a bug caught in
      // review) - clear it once the list is authoritative again. A refetch
      // failure here is a separate, transient concern (already surfaced by
      // the conversationsError toast effect above) and must not be reported
      // as the rename itself having failed, nor prevent clearing the
      // override - `conversations` still holds the last-good list either way.
      try {
        await dispatch(fetchChatConversations()).unwrap();
      } finally {
        clearTitleOverride(conversation.conversationId);
      }
    },
    [dispatch, renameDraft, clearTitleOverride],
  );

  // If the conversation being renamed is deleted, or simply drops out of a
  // refetched list, while renamingId is still set, the input was left stuck
  // in edit mode for a row that no longer exists (a bug caught in review).
  useEffect(() => {
    if (!renamingId) return;
    const stillExists = conversations.some(
      (c) => c.conversationId === renamingId,
    );
    if (!stillExists) setRenamingId(null);
  }, [conversations, renamingId]);

  // Clicking away from the rename input previously left it stuck in edit
  // mode indefinitely - there was no blur handler at all (a bug caught in
  // review). Commits the same way Enter does; a click on the save button
  // itself (which also blurs the input) still runs through commitRename via
  // its own onClick first, so this just needs to be safe to call again.
  const handleRenameBlur = useCallback(
    (conversation) => {
      commitRename(conversation);
    },
    [commitRename],
  );

  // Confirmed rather than immediate: deleting a conversation removes every
  // message in it (the API cascades) and there is no undo.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // `deleting` is render-level state - two clicks dispatched before React
  // commits the first setDeleting(true) both see deleting=false and both
  // fire a DELETE request (a bug caught in review). A ref is checked and set
  // synchronously, ahead of any render, so the second click within the same
  // tick sees the guard already up. Kept alongside `deleting`, not instead of
  // it - the ref only prevents the double-dispatch, the state still drives
  // the disabled/"Deleting..." UI.
  const deletingRef = useRef(false);

  const handleDeleteConversation = useCallback((conversation) => {
    setPendingDelete(conversation);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete || deletingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      const wasActive = pendingDelete.conversationId === activeConversationId;
      await dispatch(
        removeChatConversation(pendingDelete.conversationId),
      ).unwrap();
      // The reducer already clears activeConversationId/activeMessages when
      // the deleted conversation was active, but HelpChatPanel captures
      // conversationId into a ref at mount and never re-reads it - without
      // remounting, it keeps posting subsequent messages into the
      // now-deleted conversation (a bug caught in review).
      if (wasActive) {
        setPanelInstance((n) => n + 1);
        if (routeConversationId) navigate(pageURLs.agentChat);
      }
      clearTitleOverride(pendingDelete.conversationId);
      setPendingDelete(null);
    } catch (err) {
      // Leave the dialog open on failure so the row does not silently survive
      // a delete the user believes succeeded, and say so instead of a bare
      // disabled-then-enabled button with no explanation.
      toast.error(
        err?.response?.data?.message || err?.message || "Could not delete chat. Try again.",
      );
    } finally {
      setDeleting(false);
      deletingRef.current = false;
    }
  }, [
    dispatch,
    pendingDelete,
    activeConversationId,
    navigate,
    routeConversationId,
    clearTitleOverride,
  ]);

  const handleSelectConversation = useCallback(
    (conversationId) => {
      setDrawerOpen(false);
      if (conversationId === DRAFT_CONVERSATION_ID) return;
      // The endpoint builders in constants/v2/endPoints.js already
      // encodeURIComponent a conversation id before it hits the API; the
      // client-side route wasn't given the same treatment (a bug caught in
      // review).
      navigate(`${pageURLs.agentChat}/${encodeURIComponent(conversationId)}`);
    },
    [navigate],
  );

  // Fired by HelpChatPanel once, the moment a brand-new conversation's real
  // id comes back from the server (its first turn's `done` event) - not
  // optimistically on send, so this is never racing the server's own
  // persistence the way an immediate refetch would. Marks it active (so the
  // sidebar highlights it without waiting on the list refetch below) without
  // touching activeMessages/panelInstance - HelpChatPanel is already
  // mid-conversation and must not be remounted out from under itself.
  const handleConversationStarted = useCallback(
    (conversationId) => {
      selfStartedRef.current.add(conversationId);
      dispatch(conversationStarted(conversationId));
      dispatch(fetchChatConversations());
      setDraftConversation(null);
      // See handleSelectConversation - same client-side route needs the same
      // encoding the API endpoint builders already apply.
      navigate(`${pageURLs.agentChat}/${encodeURIComponent(conversationId)}`, {
        replace: true,
      });
    },
    [dispatch, navigate],
  );

  const handleEscalate = useCallback((transcript) => {
    setTicketModal({ show: true, transcript });
  }, []);

  const showConversationLoading =
    activeConversationId && activeMessagesLoading && !activeMessages;
  // Distinct from "no messages yet" (activeMessages === []): the reducer
  // leaves activeMessages null on a failed load specifically so this can be
  // told apart and shown, instead of silently rendering what looks like a
  // brand-new empty conversation under a real, existing conversationId.
  const showConversationError =
    activeConversationId &&
    !activeMessagesLoading &&
    !activeMessages &&
    activeMessagesError;

  return (
    <PageWrapper className="chat-page-wrapper">
      <div className="chat-page" ref={pageRef}>
        {drawerOpen && (
          <div
            className="chat-page-drawer-overlay"
            onClick={() => setDrawerOpen(false)}
          />
        )}

        <aside
          className={`chat-page-history${drawerOpen ? " chat-page-history--open" : ""}`}
        >
          {/* The product's name, once, where a product name belongs: above
              the actions rather than competing with them. Minimal on
              purpose, the mark carries the colour and the word carries the
              weight. */}
          <div className="chat-page-brand">
            <span className="chat-page-brand__mark" aria-hidden="true">
              <GenieMark size={20} />
            </span>
            <span className="chat-page-brand__name">Genie</span>
            <span className="chat-page-beta">Beta</span>
          </div>
          <div className="chat-page-history-header">
            <button
              type="button"
              className="chat-page-new-btn"
              onClick={handleNewChat}
            >
              <Plus size={16} strokeWidth={2} />
              New chat
            </button>
            <button
              type="button"
              className="chat-page-drawer-close"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close chat history"
            >
              <X size={18} strokeWidth={1.8} />
            </button>
          </div>

          <div className="chat-page-search">
            <Search size={14} strokeWidth={2} />
            <input
              type="text"
              placeholder="Search chat history"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search chat history"
            />
          </div>

          <div className="chat-page-history-list">
            {conversationsLoading ? (
              <div className="chat-page-history-empty">Loading...</div>
            ) : filteredConversations.length === 0 ? (
              <div className="chat-page-history-empty">No chats found</div>
            ) : (
              conversationGroups.map((group) => (
                <div key={group.key} className="chat-page-history-group">
                  <div className="chat-page-history-group-label">
                    {group.label}
                  </div>
                  {group.items.map((c) => {
                    const isDraft = c.conversationId === DRAFT_CONVERSATION_ID;
                    const isActive = isDraft
                      ? !activeConversationId
                      : c.conversationId === activeConversationId;
                    if (renamingId === c.conversationId) {
                      return (
                        <div
                          key={c.conversationId}
                          className="chat-page-history-row chat-page-history-row--renaming"
                        >
                          <input
                            type="text"
                            value={renameDraft}
                            onChange={(e) => setRenameDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") commitRename(c);
                              if (e.key === "Escape") {
                                // Marks this session committed too, so the
                                // blur this Escape triggers (as the input
                                // unmounts) doesn't run commitRename and
                                // resurrect a rename the user just cancelled.
                                renameCommittedRef.current = true;
                                setRenamingId(null);
                              }
                            }}
                            onBlur={() => handleRenameBlur(c)}
                            aria-label="Chat name"
                            autoFocus
                          />
                          <button
                            type="button"
                            className="chat-page-history-rename"
                            aria-label="Save chat name"
                            onClick={() => commitRename(c)}
                          >
                            <Check size={14} strokeWidth={2} aria-hidden="true" />
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={c.conversationId}
                        className={`chat-page-history-row${isActive ? " active" : ""}`}
                      >
                        <button
                          type="button"
                          className="chat-page-history-item"
                          onClick={() => handleSelectConversation(c.conversationId)}
                        >
                          <span className="chat-page-history-item-title">
                            {c.title || "New chat"}
                          </span>
                        </button>
                        {!isDraft && (
                          <>
                            <button
                              type="button"
                              className="chat-page-history-rename"
                              aria-label={`Rename chat: ${c.title || "New chat"}`}
                              onClick={() => beginRename(c)}
                            >
                              <Pencil size={14} strokeWidth={2} aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="chat-page-history-delete"
                              aria-label={`Delete chat: ${c.title || "New chat"}`}
                              onClick={() => handleDeleteConversation(c)}
                            >
                              <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          <p className="chat-page-history-retention">
            Chats are kept for 14 days.
          </p>
        </aside>

        <main className="chat-page-main">
          <div className="chat-page-main-header">
            <button
              type="button"
              className="chat-page-menu-btn"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open chat history"
            >
              <Menu size={20} strokeWidth={1.8} />
            </button>
            <span>Genie</span>
            <span className="chat-page-beta">Beta</span>
          </div>

          <div className="chat-page-main-body">
            {showConversationLoading ? (
              <div className="chat-page-history-empty">
                Loading conversation...
              </div>
            ) : showConversationError ? (
              <div className="chat-page-history-empty">
                Couldn't load this chat. Try again, or start a new one.
              </div>
            ) : (
              <HelpChatPanel
                key={panelInstance}
                layout="page"
                initialMessages={
                  activeConversationId ? activeMessages : undefined
                }
                conversationId={activeConversationId}
                onConversationStarted={handleConversationStarted}
                onConversationDraft={handleConversationDraft}
                onConversationDraftFailed={handleConversationDraftFailed}
                onEscalateToTicket={handleEscalate}
              />
            )}
          </div>
        </main>
      </div>

      <DeleteChatModal

        show={!!pendingDelete}

        title={pendingDelete?.title}

        deleting={deleting}

        onCancel={() => setPendingDelete(null)}

        onConfirm={confirmDelete}

      />

      <SupportTicketModal
        show={ticketModal.show}
        onHide={() => setTicketModal({ show: false, transcript: "" })}
        initialTranscript={ticketModal.transcript}
      />
    </PageWrapper>
  );
};

export default ChatPage;
